/** Estilos compartidos — alineados al tema Swift (login / drawer / Montserrat). */

const PRIMARY = "#07468E";
const PRIMARY_DARK = "#053a73";
const NAV = "#000922";
const INFO = "#31CDE8";
const TEXT_PRIMARY = "#0f172a";
const TEXT_SECONDARY = "#64748b";
const TEXT_MUTED = "#94a3b8";
const BORDER = "rgba(7, 70, 142, 0.12)";
const BORDER_SOFT = "rgba(15, 23, 42, 0.08)";
const PANEL_BG = "#ffffff";
const SURFACE = "#f8fafc";
const SURFACE_HOVER = "#f1f5f9";

/** Altura compartida: franja superior del drawer + barra principal */
export const APP_HEADER_HEIGHT = 64;

export const drawerHeaderBarSx = {
  minHeight: APP_HEADER_HEIGHT,
  height: APP_HEADER_HEIGHT,
  boxSizing: "border-box",
  background: "linear-gradient(180deg, #000510 0%, #000922 100%)",
  borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
};

export const appTopBarSx = {
  minHeight: APP_HEADER_HEIGHT,
  height: APP_HEADER_HEIGHT,
  boxSizing: "border-box",
  bgcolor: PANEL_BG,
  backgroundImage: `linear-gradient(180deg, ${PANEL_BG} 0%, ${SURFACE} 100%)`,
  borderBottom: `1px solid ${BORDER}`,
  color: TEXT_PRIMARY,
};

export const listPageSx = {
  width: "100%",
  pb: 3,
};

export const pageTitleAccentSx = {
  width: 4,
  flexShrink: 0,
  borderRadius: 999,
  bgcolor: PRIMARY,
  boxShadow: "0 0 12px rgba(7, 70, 142, 0.35)",
  my: 0.35,
};

export const sectionTitleAccentSx = {
  width: 3,
  height: 18,
  borderRadius: 999,
  bgcolor: INFO,
  flexShrink: 0,
};

const panelTopAccent = {
  content: '""',
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 3,
  background: `linear-gradient(90deg, ${PRIMARY} 0%, ${INFO} 100%)`,
  opacity: 0.9,
};

export const listHeaderSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: 2,
};

export const listTitleSx = {
  fontWeight: 700,
  fontSize: { xs: "1.125rem", sm: "1.375rem" },
  color: NAV,
  letterSpacing: "-0.02em",
  lineHeight: 1.25,
};

export const listSubtitleSx = {
  mt: 0.75,
  color: TEXT_SECONDARY,
  fontSize: "0.875rem",
  fontWeight: 500,
  lineHeight: 1.55,
  maxWidth: 560,
};

export const listPanelSx = {
  borderRadius: "14px",
  border: `1px solid ${BORDER}`,
  bgcolor: PANEL_BG,
  boxShadow: `0 4px 24px ${BORDER_SOFT}, 0 1px 2px rgba(0, 9, 34, 0.04)`,
  overflow: "hidden",
  position: "relative",
};

export const listFilterPanelSx = {
  ...listPanelSx,
  p: { xs: 2, sm: 2.5 },
  pt: { xs: 2.75, sm: 3.25 },
  "&::before": panelTopAccent,
};

export const listFilterLabelSx = {
  fontSize: "0.68rem",
  fontWeight: 600,
  color: TEXT_MUTED,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  mb: 0.75,
  display: "block",
};

export const listFilledFieldSx = {
  "& .MuiInputLabel-root": {
    fontSize: "0.8125rem",
    color: TEXT_SECONDARY,
    fontWeight: 500,
  },
  "& .MuiFilledInput-root, & .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: SURFACE,
    fontSize: "0.875rem",
    fontWeight: 500,
    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
    "&:before": { borderBottom: "none" },
    "&:after": { borderBottom: "none" },
    "& fieldset": { border: "none" },
    "&:hover": {
      bgcolor: SURFACE_HOVER,
    },
    "&.Mui-focused": {
      bgcolor: PANEL_BG,
      boxShadow: `0 0 0 3px rgba(7, 70, 142, 0.14)`,
    },
  },
  "& .MuiOutlinedInput-root:hover fieldset": { border: "none" },
  "& .MuiOutlinedInput-root.Mui-focused fieldset": { border: "none" },
};

export const listDateSlotProps = {
  textField: {
    variant: "filled",
    size: "small",
    fullWidth: true,
    hiddenLabel: true,
    InputLabelProps: { shrink: true },
    sx: {
      ...listFilledFieldSx,
      width: "100%",
      maxWidth: "100%",
    },
  },
};

export const listDatePickerSx = {
  width: "100%",
  maxWidth: "100%",
  display: "block",
  "& .MuiFormControl-root, & .MuiPickersTextField-root": {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
};

export const listTablePanelSx = {
  ...listPanelSx,
  "&::before": panelTopAccent,
};

export const listTableToolbarSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1.5,
  px: 2.5,
  py: 1.75,
  bgcolor: SURFACE,
  borderBottom: `1px solid ${BORDER_SOFT}`,
};

export const listTableSx = {
  "& .MuiTableHead-root .MuiTableCell-root": {
    bgcolor: SURFACE,
    color: NAV,
    fontWeight: 600,
    fontSize: "0.8125rem",
    textTransform: "none",
    letterSpacing: "0.01em",
    borderBottom: `1px solid ${BORDER}`,
    py: 1.6,
    whiteSpace: "nowrap",
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    borderBottom: `1px solid ${BORDER_SOFT}`,
    py: 1.65,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: TEXT_PRIMARY,
  },
  "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
    bgcolor: "rgba(248, 250, 252, 0.6)",
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    bgcolor: "rgba(7, 70, 142, 0.04) !important",
  },
  "& .MuiTableSortLabel-root": {
    color: `${NAV} !important`,
    "&:hover": {
      color: `${PRIMARY} !important`,
    },
    "&.Mui-active": {
      color: `${PRIMARY} !important`,
      fontWeight: 700,
    },
  },
  "& .MuiTableSortLabel-icon": {
    color: `${TEXT_MUTED} !important`,
  },
  "& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon": {
    color: `${INFO} !important`,
  },
};

export const listTableScrollSx = {
  overflow: "auto",
  maxHeight: { xs: "none", lg: "calc(100vh - 380px)" },
  "&::-webkit-scrollbar": { width: 5, height: 5 },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 999,
    backgroundColor: "rgba(7, 70, 142, 0.22)",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
};

export const listIdChipSx = {
  height: 26,
  fontFamily: "ui-monospace, SFMono-Regular, monospace",
  fontSize: "0.75rem",
  fontWeight: 600,
  bgcolor: SURFACE,
  color: PRIMARY,
  border: `1px solid ${BORDER}`,
};

export const listActionBtnSx = (accent) => ({
  width: 36,
  height: 36,
  borderRadius: "10px",
  border: `1px solid ${BORDER}`,
  bgcolor: PANEL_BG,
  color: accent ?? PRIMARY,
  transition: "all 0.18s ease",
  boxShadow: "0 1px 2px rgba(0, 9, 34, 0.04)",
  "&:hover": {
    bgcolor: SURFACE,
    borderColor: accent ?? PRIMARY,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px rgba(0, 9, 34, 0.1)`,
  },
});

export const listPrimaryBtnSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  px: 2.5,
  py: 0.95,
  bgcolor: PRIMARY,
  color: "#ffffff",
  boxShadow: `0 4px 14px rgba(7, 70, 142, 0.28)`,
  "&:hover": {
    bgcolor: PRIMARY_DARK,
    color: "#ffffff",
    boxShadow: `0 6px 18px rgba(7, 70, 142, 0.32)`,
  },
  "&.Mui-disabled": {
    bgcolor: TEXT_MUTED,
    color: "#ffffff",
    boxShadow: "none",
  },
};

export const listClearBtnSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  color: TEXT_SECONDARY,
  px: 2,
  "&:hover": {
    bgcolor: SURFACE,
    color: NAV,
  },
};

export const listExportBtnSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  color: PRIMARY,
  borderColor: BORDER,
  bgcolor: PANEL_BG,
  px: 2,
  "&:hover": {
    bgcolor: SURFACE,
    borderColor: PRIMARY,
    color: PRIMARY_DARK,
  },
};

export const listAddBtnSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  bgcolor: PRIMARY,
  color: "#ffffff",
  boxShadow: `0 4px 14px rgba(7, 70, 142, 0.28)`,
  "&:hover": {
    bgcolor: PRIMARY_DARK,
    color: "#ffffff",
    boxShadow: `0 6px 18px rgba(7, 70, 142, 0.32)`,
  },
};

export const listHeaderAddBtnSx = {
  width: 42,
  height: 42,
  borderRadius: "10px",
  bgcolor: PRIMARY,
  color: "#ffffff",
  border: `1px solid ${PRIMARY}`,
  boxShadow: `0 4px 14px rgba(7, 70, 142, 0.28)`,
  transition: "all 0.18s ease",
  "& .MuiSvgIcon-root": {
    color: "#ffffff",
  },
  "&:hover": {
    bgcolor: PRIMARY_DARK,
    borderColor: PRIMARY_DARK,
    color: "#ffffff",
    transform: "translateY(-1px)",
    boxShadow: `0 6px 18px rgba(7, 70, 142, 0.32)`,
    "& .MuiSvgIcon-root": {
      color: "#ffffff",
    },
  },
};

export const listPaginationSx = {
  borderTop: `1px solid ${BORDER_SOFT}`,
  bgcolor: SURFACE,
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontWeight: 500,
    fontSize: "0.8125rem",
    color: TEXT_SECONDARY,
  },
  "& .MuiTablePagination-select": {
    fontWeight: 600,
    fontSize: "0.8125rem",
  },
  "& .MuiIconButton-root": {
    borderRadius: "8px",
    "&.Mui-disabled": {
      opacity: 0.4,
    },
  },
};

/** Pantallas de detalle (mismo lenguaje visual que listados). */
export const detailPageSx = listPageSx;

export const detailSectionSx = {
  ...listPanelSx,
  p: { xs: 2, sm: 2.5 },
  pt: { xs: 2.75, sm: 3.25 },
  "&::before": {
    ...panelTopAccent,
    opacity: 0.75,
  },
};

export const detailSectionTitleSx = {
  fontWeight: 700,
  fontSize: "0.9375rem",
  color: NAV,
  letterSpacing: "-0.01em",
};

export const detailSectionHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1,
  mb: 2,
  pb: 1.5,
  borderBottom: `1px solid ${BORDER_SOFT}`,
};

export const formActionsSx = {
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 1,
  mt: 2.5,
  pt: 2.5,
  borderTop: `1px solid ${BORDER_SOFT}`,
};

export const detailReadOnlyFieldSx = {
  ...listFilledFieldSx,
  "& .MuiFilledInput-root": {
    borderRadius: "10px",
    bgcolor: SURFACE,
    fontSize: "0.875rem",
    pointerEvents: "none",
    "&:before": { borderBottom: "none" },
    "&:after": { borderBottom: "none" },
    "& fieldset": { border: "none" },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: TEXT_PRIMARY,
    color: TEXT_PRIMARY,
    fontWeight: 500,
  },
};

export const detailEmptyStateSx = {
  textAlign: "center",
  py: 5,
  color: TEXT_SECONDARY,
  fontWeight: 500,
};

export const detailImageThumbSx = {
  width: "100%",
  maxHeight: 160,
  objectFit: "cover",
  borderRadius: "10px",
  cursor: "pointer",
  border: `1px solid ${BORDER}`,
  transition: "box-shadow 0.18s ease, transform 0.18s ease",
  "&:hover": {
    boxShadow: `0 8px 24px ${BORDER_SOFT}`,
    transform: "translateY(-1px)",
  },
};

/** Autocomplete en formularios Agregar/Editar (mismo estilo que filtros). */
export const formAutocompleteSlotProps = {
  textField: {
    variant: "filled",
    size: "small",
    fullWidth: true,
    hiddenLabel: true,
    sx: listFilledFieldSx,
  },
};

/** Acordeones en Permisos por Usuario */
export const permissionAccordionSx = {
  border: `1px solid ${BORDER}`,
  borderRadius: "10px",
  boxShadow: "none",
  mb: 1,
  overflow: "hidden",
  "&:before": { display: "none" },
  "&.Mui-expanded": {
    margin: 0,
    mb: 1,
  },
};

export const permissionNestedAccordionSx = {
  ...permissionAccordionSx,
  bgcolor: SURFACE,
  ml: { xs: 0, sm: 1 },
};

/** Fila de acción (nivel 3) en permisos web */
export const permissionActionRowSx = {
  display: "flex",
  alignItems: "center",
  minHeight: 40,
  pl: 2,
  pr: 1,
  py: 0.5,
  borderBottom: `1px solid ${BORDER_SOFT}`,
  "&:last-child": { borderBottom: "none" },
  "& .MuiFormControlLabel-root": {
    m: 0,
    flex: 1,
    alignItems: "center",
  },
  "& .MuiFormControlLabel-label": {
    lineHeight: 1.4,
    fontWeight: 500,
  },
};

export const permissionActionListSx = {
  pl: { xs: 1, sm: 2 },
  pr: 0.5,
  pt: 0.5,
  pb: 0.5,
};

export const permissionTabsSx = {
  mb: 2,
  minHeight: 44,
  borderBottom: `1px solid ${BORDER_SOFT}`,
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    color: TEXT_SECONDARY,
    minHeight: 44,
    px: 2,
  },
  "& .Mui-selected": {
    color: PRIMARY,
    fontWeight: 700,
  },
  "& .MuiTabs-indicator": {
    backgroundColor: INFO,
    height: 3,
    borderRadius: "3px 3px 0 0",
  },
};

export const permissionCheckboxSx = {
  color: TEXT_MUTED,
  "&.Mui-checked": {
    color: PRIMARY,
  },
};

export const detailImageModalSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: "90vw",
  maxHeight: "90vh",
  outline: "none",
};

/** @deprecated usar listPageSx */
export const pageCardSx = listPanelSx;

/** @deprecated usar listFilterPanelSx */
export const filterSectionSx = listFilterPanelSx;

/** @deprecated usar listFilledFieldSx */
export const filterFieldSx = listFilledFieldSx;

/** @deprecated usar listDateSlotProps */
export const datePickerSlotProps = listDateSlotProps;

/** @deprecated usar listTableScrollSx */
export const tableScrollContainerSx = listTableScrollSx;

export const stickyFirstColumnSx = () => ({});
